import { useEffect, useRef } from 'react';
import { getVideoElementFromRef } from '@/utils/video-player';
import { TimeDomain } from '@/types/video-player';
import { useVideoPlayerContext } from '../VideoPlayerProvider';

export function useVideoLoopPlayback() {
    const {
        activeMediaRef,
        emitter,
        loop,
        loopSeekingRef,
        loopTimeRange,
        setLoop,
        streaming,
    } = useVideoPlayerContext();
    const seekingToLoopStartRef = useRef(false);

    useEffect(() => {
        seekingToLoopStartRef.current = false;

        const videoElement = getVideoElementFromRef(activeMediaRef);
        if (videoElement) videoElement.loop = false;

        if (!loopTimeRange) {
            const unsubscribeEnded = emitter.on('ended', () => {
                if (loop) {
                    const videoElement = getVideoElementFromRef(activeMediaRef);
                    if (!videoElement) return;
                    loopSeekingRef.current = true;
                    videoElement.currentTime = 0;
                    videoElement.play();
                }
            });

            return unsubscribeEnded;
        }

        const { endTime, startTime, playbackStart, domain } = loopTimeRange;
        const streamBeginTimestamp =
            streaming.streamBeginTimestamp ?? undefined;

        const isTimelineMode =
            domain === TimeDomain.ABSOLUTE && streamBeginTimestamp != null;

        const getRelativeTime = (absoluteTime: number): number => {
            if (!isTimelineMode) return absoluteTime;
            return absoluteTime - streamBeginTimestamp!;
        };

        const getCurrentAbsoluteTime = (): number => {
            const videoElement = getVideoElementFromRef(activeMediaRef);
            if (!isTimelineMode || !videoElement) {
                return videoElement?.currentTime ?? 0;
            }
            return streamBeginTimestamp! + videoElement.currentTime;
        };

        const relativePlaybackStart = getRelativeTime(playbackStart);

        const isPlaybackStartInCurrentWindow = (): boolean => {
            const videoElement = getVideoElementFromRef(activeMediaRef);
            const videoDuration = videoElement?.duration || 0;
            return (
                relativePlaybackStart >= 0 &&
                relativePlaybackStart < videoDuration
            );
        };

        const unsubscribeTimeUpdate = emitter.on('timeupdate', () => {
            if (!loop || seekingToLoopStartRef.current) return;
            const videoElement = getVideoElementFromRef(activeMediaRef);
            if (!videoElement) return;

            if (isTimelineMode) {
                const absoluteCurrentTime = getCurrentAbsoluteTime();
                const isOutsideRange =
                    absoluteCurrentTime < startTime ||
                    absoluteCurrentTime >= endTime;

                if (isOutsideRange) {
                    if (isPlaybackStartInCurrentWindow()) {
                        loopSeekingRef.current = true;
                        videoElement.currentTime = relativePlaybackStart;
                    } else if (streaming.onSeekToTimestamp) {
                        loopSeekingRef.current = true;
                        seekingToLoopStartRef.current = true;
                        streaming.onSeekToTimestamp(playbackStart);
                    }
                }
                return;
            }

            if (domain !== TimeDomain.MEDIA) return;

            if (
                videoElement.currentTime < startTime ||
                videoElement.currentTime >= endTime
            ) {
                loopSeekingRef.current = true;
                videoElement.currentTime = playbackStart;
            }
        });

        const unsubscribeSeeked = emitter.on('seeked', () => {
            seekingToLoopStartRef.current = false;

            if (!loop) return;

            const videoElement = getVideoElementFromRef(activeMediaRef);
            if (!videoElement) return;

            if (isTimelineMode) {
                const absoluteCurrentTime = getCurrentAbsoluteTime();
                if (
                    Math.ceil(absoluteCurrentTime) < startTime ||
                    absoluteCurrentTime > endTime
                ) {
                    if (streaming.onSeekToTimestamp) {
                        loopSeekingRef.current = true;
                        seekingToLoopStartRef.current = true;
                        streaming.onSeekToTimestamp(playbackStart);
                    } else {
                        setLoop(false);
                    }
                }
                return;
            }

            if (domain !== TimeDomain.MEDIA) return;

            if (
                Math.ceil(videoElement.currentTime) < startTime ||
                videoElement.currentTime > endTime
            ) {
                setLoop(false);
            }
        });

        return () => {
            unsubscribeTimeUpdate();
            unsubscribeSeeked();
        };
    }, [
        activeMediaRef,
        emitter,
        loop,
        loopSeekingRef,
        loopTimeRange,
        setLoop,
        streaming.streamBeginTimestamp,
        streaming.onSeekToTimestamp,
    ]);
}
